import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { client } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COMMON_TRIGGERS, GETAWAY_TYPES, ENERGY_LEVELS, ACTIVITY_PREFS } from "@/data/content";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Map, Compass, Repeat, Sparkles, Quote, RefreshCw, Plus, X, Car, Tent, Sofa, Mountain, Home,
  Sun, Sunrise, Moon, Leaf, ListChecks, Target, Route, MapPin, Info, ExternalLink, Trash2,
} from "lucide-react";

const GETAWAY_ICON = { Car, Tent, Sofa, Mountain, Home };
const DAY_ICON = { 1: Sunrise, 2: Sun, 3: Sun, 4: Moon };
const ENERGY_PILL = {
  low: { label: "Low energy", cls: "bg-sky/15 text-sky" },
  medium: { label: "Moderate", cls: "bg-gold/15 text-gold" },
  high: { label: "Active", cls: "bg-terracotta/10 text-terracotta" },
};
const bookingUrl = (q, region) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${q}${region ? ` near ${region}` : ""}`)}`;

function LoadingState() {
  return (
    <div className="rounded-3xl bg-card border border-border p-12 shadow-soft text-center" data-testid="strategy-generating">
      <motion.div
        className="rounded-full bg-sage w-16 h-16 flex items-center justify-center mx-auto mb-6"
        animate={{ rotate: 360 }} transition={{ duration: 8, repeat: Infinity, ease: "linear" }}>
        <Compass className="w-7 h-7 text-sky" />
      </motion.div>
      <h2 className="font-serif text-3xl text-olive mb-2">Charting your course…</h2>
      <p className="text-olive/70 max-w-md mx-auto">
        Your Companion is mapping your triggers, new habits, and a 4-night plan. This takes a moment — it's worth the wait.
      </p>
    </div>
  );
}

function AreasLoadingState() {
  return (
    <div className="rounded-3xl bg-card border border-border p-12 shadow-soft text-center" data-testid="areas-generating">
      <motion.div
        className="rounded-full bg-sage w-16 h-16 flex items-center justify-center mx-auto mb-6"
        animate={{ rotate: 360 }} transition={{ duration: 8, repeat: Infinity, ease: "linear" }}>
        <MapPin className="w-7 h-7 text-terracotta" />
      </motion.div>
      <h2 className="font-serif text-3xl text-olive mb-2">Scouting gentle getaways…</h2>
      <p className="text-olive/70 max-w-md mx-auto">
        Finding a few restful spots near you that fit your energy and what you love.
      </p>
    </div>
  );
}

export default function StrategyPage() {
  const { user } = useAuth();
  const [strategies, setStrategies] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [checked, setChecked] = useState({});
  const [custom, setCustom] = useState("");
  const [areaOptions, setAreaOptions] = useState([]);
  const [pickingArea, setPickingArea] = useState(false);
  const [loadingAreas, setLoadingAreas] = useState(false);

  const [form, setForm] = useState({
    triggers: ["Morning coffee", "After meals", "Driving"],
    habit_goals: "",
    getaway_type: "staycation",
    getaway_notes: "",
    location: "",
    energy_level: "low",
    activity_prefs: ["Spa & wellness", "Hot springs", "Mild hikes"],
    area: "",
    quit_date: "",
    plan_id: null,
  });

  const load = useCallback(async () => {
    try {
      const [sRes, pRes] = await Promise.all([
        client.get("/strategies"),
        client.get("/plans"),
      ]);
      const cig = (pRes.data || []).find((p) => p.substance === "cigarettes") || (pRes.data || [])[0];
      if (cig) setForm((f) => ({ ...f, quit_date: f.quit_date || cig.quit_date, plan_id: cig.id }));
      const list = sRes.data || [];
      setStrategies(list);
      if (list.length) {
        setData(list[0]);
        setChecked(list[0].checked || {});
        setBuilding(false);
      } else {
        setData(null);
        setBuilding(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleTrigger = (t) =>
    setForm((f) => ({ ...f, triggers: f.triggers.includes(t) ? f.triggers.filter((x) => x !== t) : [...f.triggers, t] }));

  const addCustom = () => {
    const t = custom.trim();
    if (t && !form.triggers.includes(t)) setForm((f) => ({ ...f, triggers: [...f.triggers, t] }));
    setCustom("");
  };

  const toggleActivity = (a) =>
    setForm((f) => ({ ...f, activity_prefs: f.activity_prefs.includes(a) ? f.activity_prefs.filter((x) => x !== a) : [...f.activity_prefs, a] }));

  const generate = async () => {
    if (!form.triggers.length) { toast.error("Pick at least one trigger to plan around."); return; }
    setPickingArea(false);
    setGenerating(true);
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    try {
      const res = await client.post("/strategy", {
        substance: "cigarettes",
        quit_date: form.quit_date || null,
        plan_id: form.plan_id,
        triggers: form.triggers,
        habit_goals: form.habit_goals,
        getaway_type: form.getaway_type,
        getaway_notes: form.getaway_notes,
        location: form.location,
        energy_level: form.energy_level,
        activity_prefs: form.activity_prefs,
        area: form.area,
      });
      const jobId = res.data.job_id;
      const start = Date.now();
      while (Date.now() - start < 180000) {
        await sleep(3000);
        const st = await client.get(`/strategy/status/${jobId}`);
        if (st.data.status === "done" && st.data.strategy) {
          const created = st.data.strategy;
          try {
            const lRes = await client.get("/strategies");
            setStrategies(lRes.data || []);
          } catch (_) { /* non-fatal */ }
          setData(created);
          setChecked(created.checked || {});
          setBuilding(false);
          toast.success("Your new strategy is ready. One step at a time.");
          return;
        }
        if (st.data.status === "error") {
          throw new Error(st.data.error || "generation failed");
        }
      }
      throw new Error("timeout");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build the plan. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const findAreas = async () => {
    if (!form.triggers.length) { toast.error("Pick at least one trigger to plan around."); return; }
    // Staying home or no location — skip the area step and build a home-based plan.
    if (form.getaway_type === "none" || !form.location.trim()) {
      setForm((f) => ({ ...f, area: "" }));
      generate();
      return;
    }
    setLoadingAreas(true);
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    try {
      const res = await client.post("/strategy/areas", {
        location: form.location,
        energy_level: form.energy_level,
        activity_prefs: form.activity_prefs,
        getaway_type: form.getaway_type,
      });
      const jobId = res.data.job_id;
      const start = Date.now();
      while (Date.now() - start < 120000) {
        await sleep(2500);
        const st = await client.get(`/strategy/areas/status/${jobId}`);
        if (st.data.status === "done") {
          const areas = st.data.areas || [];
          if (!areas.length) { generate(); return; }
          setAreaOptions(areas);
          setForm((f) => ({ ...f, area: areas[0]?.name || "" }));
          setPickingArea(true);
          return;
        }
        if (st.data.status === "error") throw new Error(st.data.error || "failed");
      }
      throw new Error("timeout");
    } catch (e) {
      toast.error("Couldn't fetch area ideas — you can still build a plan.");
    } finally {
      setLoadingAreas(false);
    }
  };

  const toggleCheck = async (key) => {
    const next = { ...checked, [key]: !checked[key] };
    if (!next[key]) delete next[key];
    setChecked(next);
    setStrategies((ls) => ls.map((x) => (x.id === data.id ? { ...x, checked: next } : x)));
    try {
      await client.put(`/strategy/${data.id}/checked`, { checked: next });
    } catch (e) {
      toast.error("Couldn't save that just now.");
    }
  };

  const startNew = () => {
    setForm((f) => ({
      triggers: ["Morning coffee", "After meals", "Driving"],
      habit_goals: "",
      getaway_type: "staycation",
      getaway_notes: "",
      location: "",
      energy_level: "low",
      activity_prefs: ["Spa & wellness", "Hot springs", "Mild hikes"],
      area: "",
      quit_date: f.quit_date,
      plan_id: f.plan_id,
    }));
    setPickingArea(false);
    setAreaOptions([]);
    setBuilding(true);
  };

  const selectStrategy = (id) => {
    const st = strategies.find((x) => x.id === id);
    if (st) { setData(st); setChecked(st.checked || {}); }
  };

  const deleteStrategy = async (id) => {
    try {
      await client.delete(`/strategy/${id}`);
      const rest = strategies.filter((x) => x.id !== id);
      setStrategies(rest);
      if (rest.length) { setData(rest[0]); setChecked(rest[0].checked || {}); }
      else { setData(null); setBuilding(true); }
      toast.success("Strategy removed.");
    } catch (e) {
      toast.error("Couldn't remove that. Please try again.");
    }
  };

  if (loading) return <div className="animate-pulse text-stone" data-testid="strategy-loading">Opening your strategy…</div>;

  const Header = (
    <div>
      <p className="uppercase tracking-[0.2em] text-xs text-terracotta mb-2">Your battle plan</p>
      <h1 className="font-serif text-display text-olive">Quit strategy</h1>
      <p className="text-olive/70 mt-3 max-w-2xl">
        A gentle, mapped-out plan for the crucial first days — so you're not figuring it out in the moment.
        Build more than one and switch between them anytime.
      </p>
    </div>
  );

  // ---------- Builder ----------
  if (building || !data?.strategy) {
    return (
      <div className="space-y-8" data-testid="strategy-page">
        {Header}
        {generating ? <LoadingState /> : loadingAreas ? <AreasLoadingState /> : pickingArea ? (
          <div className="rounded-3xl bg-card border border-border p-7 shadow-soft space-y-6" data-testid="area-picker">
            <div>
              <p className="uppercase tracking-[0.2em] text-xs text-terracotta mb-2">Step 2 · Pick your place</p>
              <h2 className="font-serif text-h2 text-olive">Where would feel like a fresh start?</h2>
              <p className="text-olive/70 mt-2 max-w-2xl">
                A few gentle getaways near {form.location || "you"}, matched to your energy and what you love.
                Pick one and we'll build your whole 4-night plan around it.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {areaOptions.map((a, i) => {
                const active = form.area === a.name;
                return (
                  <div key={i} data-testid={`area-option-${i}`} onClick={() => setForm((f) => ({ ...f, area: a.name }))}
                    className={`cursor-pointer rounded-2xl border p-5 transition-colors duration-200 ${active ? "border-terracotta bg-terracotta/5" : "border-border hover:bg-sage"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-serif text-xl text-olive">{a.name}</p>
                        {a.region && <p className="text-xs text-stone">{a.region}</p>}
                      </div>
                      {active && <span className="text-[10px] rounded-full bg-terracotta text-sand px-2 py-0.5 shrink-0">Chosen</span>}
                    </div>
                    {a.why && <p className="text-sm text-olive/75 mt-2">{a.why}</p>}
                    <div className="flex items-center gap-3 mt-3 flex-wrap">
                      {a.vibe && <span className="text-[11px] rounded-full bg-sage px-2.5 py-1 text-olive/70">{a.vibe}</span>}
                      {a.distance && <span className="text-[11px] text-stone">{a.distance}</span>}
                      <a data-testid={`area-book-${i}`} href={bookingUrl(a.search || a.name, a.region || form.location)} target="_blank" rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-xs text-terracotta hover:underline ml-auto">
                        <ExternalLink className="w-3 h-3" /> Find &amp; book
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-3">
              <Button data-testid="build-plan-btn" onClick={generate} disabled={!form.area}
                className="rounded-full bg-terracotta hover:bg-terracotta-soft text-sand gap-2 px-7 py-6 disabled:opacity-50">
                <Sparkles className="w-5 h-5" /> Build my 4-night plan
              </Button>
              <button data-testid="area-back-btn" onClick={() => setPickingArea(false)} className="text-sm text-olive/60 hover:underline">Back</button>
            </div>
          </div>
        ) : (
          <div className="rounded-3xl bg-card border border-border p-7 shadow-soft space-y-7" data-testid="strategy-builder">
            <div>
              <Label className="text-olive mb-1 block">What makes you reach for a cigarette?</Label>
              <p className="text-sm text-stone mb-3">Choose the moments that trip you up. We'll plan a swap for each.</p>
              <div className="flex flex-wrap gap-2">
                {COMMON_TRIGGERS.map((t) => {
                  const active = form.triggers.includes(t);
                  return (
                    <button key={t} data-testid={`trigger-${t}`} onClick={() => toggleTrigger(t)}
                      className={`rounded-full px-4 py-2 text-sm border transition-colors duration-200 ${active ? "bg-brand-teal text-brand-navy border-brand-teal" : "bg-card text-olive/70 border-border hover:bg-sage"}`}>
                      {t}
                    </button>
                  );
                })}
                {form.triggers.filter((t) => !COMMON_TRIGGERS.includes(t)).map((t) => (
                  <button key={t} data-testid={`trigger-${t}`} onClick={() => toggleTrigger(t)}
                    className="rounded-full px-4 py-2 text-sm border bg-brand-teal text-brand-navy border-brand-teal flex items-center gap-1.5">
                    {t} <X className="w-3.5 h-3.5" />
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-3 max-w-sm">
                <Input data-testid="custom-trigger-input" value={custom} onChange={(e) => setCustom(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
                  placeholder="Add your own trigger…" className="h-9" />
                <Button data-testid="add-trigger-btn" onClick={addCustom} variant="outline" className="h-9 rounded-full border-olive/20 hover:bg-sage px-3">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="habits" className="text-olive mb-2 block">New habits you'd love to build</Label>
              <Textarea id="habits" data-testid="habit-goals-input" rows={2}
                placeholder="e.g. a morning walk, more water, calling a friend, evening prayer…"
                value={form.habit_goals} onChange={(e) => setForm((f) => ({ ...f, habit_goals: e.target.value }))} />
            </div>

            <div>
              <Label className="text-olive mb-1 block">Your first few days</Label>
              <p className="text-sm text-stone mb-3">A change of scene helps — but keep it easy. You'll be away 4 nights (so you don't head home to old habits too soon). A big road trip is optional; staying close to home works just as well.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {GETAWAY_TYPES.map((g) => {
                  const GIcon = GETAWAY_ICON[g.icon] || Map;
                  const active = form.getaway_type === g.id;
                  return (
                    <button key={g.id} data-testid={`getaway-${g.id}`} onClick={() => setForm((f) => ({ ...f, getaway_type: g.id }))}
                      className={`rounded-2xl border p-4 text-left transition-colors duration-200 ${active ? "border-terracotta bg-terracotta/5" : "border-border hover:bg-sage"}`}>
                      <GIcon className={`w-6 h-6 mb-2 ${active ? "text-terracotta" : "text-sky"}`} />
                      <p className="font-medium text-olive text-sm">{g.label}</p>
                      <p className="text-xs text-stone leading-snug mt-0.5">{g.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="loc" className="text-olive mb-1 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-terracotta" /> Where are you?
              </Label>
              <p className="text-sm text-stone mb-3">Your city or region, so we can suggest real places nearby — spas, hot springs, gentle trails.</p>
              <Input id="loc" data-testid="strategy-location-input" placeholder="e.g. Boise, Idaho"
                value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                className="max-w-md" />
            </div>

            <div>
              <Label className="text-olive mb-1 block">How much energy will you have?</Label>
              <p className="text-sm text-stone mb-3">Withdrawal can be draining. We'll match the plan's pace to how you feel.</p>
              <div className="grid sm:grid-cols-3 gap-3">
                {ENERGY_LEVELS.map((lvl) => {
                  const active = form.energy_level === lvl.id;
                  return (
                    <button key={lvl.id} data-testid={`energy-${lvl.id}`} onClick={() => setForm((f) => ({ ...f, energy_level: lvl.id }))}
                      className={`rounded-2xl border p-4 text-left transition-colors duration-200 ${active ? "border-terracotta bg-terracotta/5" : "border-border hover:bg-sage"}`}>
                      <p className="font-medium text-olive text-sm">{lvl.label}</p>
                      <p className="text-xs text-stone leading-snug mt-0.5">{lvl.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-olive mb-1 block">What sounds restful and good?</Label>
              <p className="text-sm text-stone mb-3">Pick a few — we'll weave your favorites into your 4-night plan.</p>
              <div className="flex flex-wrap gap-2">
                {ACTIVITY_PREFS.map((a) => {
                  const active = form.activity_prefs.includes(a);
                  return (
                    <button key={a} data-testid={`activity-${a}`} onClick={() => toggleActivity(a)}
                      className={`rounded-full px-4 py-2 text-sm border transition-colors duration-200 ${active ? "bg-brand-teal text-brand-navy border-brand-teal" : "bg-card text-olive/70 border-border hover:bg-sage"}`}>
                      {a}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="notes" className="text-olive mb-2 block">Getaway details (optional)</Label>
                <Input id="notes" data-testid="getaway-notes-input" placeholder="Where, and who with?"
                  value={form.getaway_notes} onChange={(e) => setForm((f) => ({ ...f, getaway_notes: e.target.value }))} />
              </div>
              <div>
                <Label htmlFor="qd" className="text-olive mb-2 block">Quit day (optional)</Label>
                <Input id="qd" data-testid="strategy-quitdate" type="date" value={form.quit_date || ""}
                  onChange={(e) => setForm((f) => ({ ...f, quit_date: e.target.value }))} />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-3">
                {form.getaway_type === "none" ? (
                  <Button data-testid="generate-strategy-btn" onClick={generate} className="rounded-full bg-terracotta hover:bg-terracotta-soft text-sand gap-2 px-7 py-6">
                    <Home className="w-5 h-5" /> Build my at-home plan
                  </Button>
                ) : (
                  <Button data-testid="generate-strategy-btn" onClick={findAreas} className="rounded-full bg-terracotta hover:bg-terracotta-soft text-sand gap-2 px-7 py-6">
                    <Compass className="w-5 h-5" /> Find getaway ideas near me
                  </Button>
                )}
                {data?.strategy && (
                  <button onClick={() => setBuilding(false)} className="text-sm text-olive/60 hover:underline">Cancel</button>
                )}
              </div>
              <p className="text-xs text-stone">
                {form.getaway_type === "none"
                  ? "We'll build a gentle 4-day keep-busy plan you can do right from home — no travel needed."
                  : "We'll suggest a few gentle getaways near you first — then build your full plan around the one you pick. Prefer to stay home? Choose \u201CAt home\u201D above."}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------- Display ----------
  const s = data.strategy;
  const g = s.getaway || {};

  return (
    <div className="space-y-8" data-testid="strategy-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        {Header}
        <div className="flex items-center gap-2">
          <Button data-testid="new-strategy-btn" onClick={startNew} className="rounded-full bg-terracotta hover:bg-terracotta-soft text-sand gap-2">
            <Plus className="w-4 h-4" /> New strategy
          </Button>
          <button data-testid="delete-strategy-btn" onClick={() => deleteStrategy(data.id)} title="Delete this strategy"
            className="rounded-full p-2.5 text-stone border border-border hover:bg-terracotta/10 hover:text-terracotta transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {strategies.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="strategy-switcher">
          {strategies.map((st) => {
            const active = st.id === data.id;
            const raw = st.strategy?.getaway?.title || st.inputs?.area || (st.inputs?.getaway_type === "none" ? "At-home plan" : "Getaway plan");
            const label = raw.length > 42 ? raw.slice(0, 42) + "…" : raw;
            return (
              <button key={st.id} data-testid={`strategy-tab-${st.id}`} onClick={() => selectStrategy(st.id)}
                className={`rounded-full px-4 py-2 text-sm border transition-colors duration-200 ${active ? "bg-brand-teal text-brand-navy border-brand-teal" : "bg-card text-olive/70 border-border hover:bg-sage"}`}>
                {label}
              </button>
            );
          })}
        </div>
      )}

      {s.summary && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl bg-brand-navy-light text-brand-ivory border border-brand-teal/20 p-7 shadow-soft" data-testid="strategy-summary">
          <p className="uppercase tracking-[0.2em] text-[11px] text-gold mb-3">Your plan</p>
          <p className="font-serif text-2xl leading-relaxed">{s.summary}</p>
        </motion.div>
      )}

      {/* First-days plan */}
      <section data-testid="strategy-getaway">
        <div className="flex items-center gap-2 mb-4">
          <Route className="w-5 h-5 text-terracotta" />
          <h2 className="font-serif text-h2 text-olive">Your gentle first-days plan</h2>
        </div>

        <div className="rounded-3xl bg-sky/10 border border-border p-7 shadow-soft mb-5">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className="uppercase tracking-[0.2em] text-[11px] text-stone">{g.type || "Getaway"}</span>
            {g.region && (
              <span data-testid="getaway-region" title={g.region} className="inline-flex items-center gap-1 rounded-full bg-card border border-border px-3 py-1 text-xs text-olive max-w-[260px]">
                <MapPin className="w-3 h-3 text-terracotta shrink-0" /> <span className="truncate">{g.region}</span>
              </span>
            )}
          </div>
          <h3 className="font-serif text-3xl text-olive mb-2">{g.title}</h3>
          <p className="text-olive/70">{g.overview}</p>

          <div className="flex items-start gap-2 rounded-2xl bg-gold/10 p-3 mt-4" data-testid="planning-note">
            <Info className="w-4 h-4 text-gold mt-0.5 shrink-0" />
            <p className="text-xs text-olive/75">
              These are planning ideas, not confirmed bookings. Tap <span className="font-medium">Find &amp; book</span> on any
              activity to look it up, check current hours, and reserve ahead.
            </p>
          </div>

          {g.prep?.length > 0 && (
            <div className="mt-5" data-testid="strategy-prep">
              <p className="flex items-center gap-2 font-medium text-olive mb-3"><ListChecks className="w-4 h-4 text-sky" /> Set up before quit day</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {g.prep.map((item, i) => {
                  const key = `prep-${i}`;
                  return (
                    <label key={i} data-testid={`prep-${i}`}
                      className="flex items-center gap-3 rounded-2xl bg-card border border-border p-3 text-left cursor-pointer transition-colors hover:bg-sage">
                      <Checkbox checked={!!checked[key]} onCheckedChange={() => toggleCheck(key)} />
                      <span className={`text-sm text-olive ${checked[key] ? "line-through opacity-60" : ""}`}>{item}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {g.packing?.length > 0 && (
            <div className="mt-5">
              <p className="flex items-center gap-2 font-medium text-olive mb-3"><ListChecks className="w-4 h-4 text-sky" /> Pack list</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {g.packing.map((item, i) => {
                  const key = `pack-${i}`;
                  return (
                    <label key={i} data-testid={`pack-${i}`}
                      className="flex items-center gap-3 rounded-2xl bg-card border border-border p-3 text-left cursor-pointer transition-colors hover:bg-sage">
                      <Checkbox checked={!!checked[key]} onCheckedChange={() => toggleCheck(key)} />
                      <span className={`text-sm text-olive ${checked[key] ? "line-through opacity-60" : ""}`}>{item}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-4">
          {(g.days || []).map((day, di) => {
            const DIcon = DAY_ICON[day.day] || Sun;
            const blocks = day.blocks?.length
              ? day.blocks
              : (day.activities || []).map((a) => ({ title: a, legacy: true }));
            return (
              <motion.div key={di} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: di * 0.06 }}
                className="rounded-3xl bg-card border border-border p-6 shadow-soft" data-testid={`getaway-day-${day.day}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="rounded-xl bg-sage p-2"><DIcon className="w-4 h-4 text-sky" /></div>
                  <span className="text-xs uppercase tracking-[0.15em] text-stone">Day {day.day}</span>
                </div>
                <h4 className="font-serif text-2xl text-olive">{day.title}</h4>
                {day.focus && <p className="text-sm text-stone mt-1">{day.focus}</p>}

                <div className="space-y-3 mb-4 mt-4">
                  {blocks.map((b, bi) => {
                    const key = `d${day.day}-blk-${bi}`;
                    const pill = ENERGY_PILL[(b.energy || "").toLowerCase()];
                    return (
                      <div key={bi} data-testid={`day-${day.day}-block-${bi}`} className="rounded-2xl border border-border p-4">
                        <div className="flex items-start gap-2.5">
                          <Checkbox checked={!!checked[key]} onCheckedChange={() => toggleCheck(key)} className="mt-1" data-testid={`day-${day.day}-block-check-${bi}`} />
                          <div className="flex-1 min-w-0">
                            {(b.time || pill) && (
                              <div className="flex items-center gap-2 flex-wrap">
                                {b.time && <span className="text-[11px] uppercase tracking-[0.12em] text-terracotta">{b.time}</span>}
                                {pill && <span className={`text-[10px] rounded-full px-2 py-0.5 ${pill.cls}`}>{pill.label}</span>}
                              </div>
                            )}
                            <p className={`font-medium text-olive mt-1 ${checked[key] ? "line-through opacity-60" : ""}`}>{b.title}</p>
                            {b.place && <p className="text-xs text-sky mt-0.5">{b.place}</p>}
                            {b.activity && <p className="text-sm text-olive/70 mt-1">{b.activity}</p>}
                            {b.why && <p className="text-xs text-stone mt-2"><span className="text-sky">Why:</span> {b.why}</p>}
                            {b.tip && <p className="text-xs text-stone mt-1"><span className="text-gold">Craving tip:</span> {b.tip}</p>}
                            {!b.legacy && (b.place || b.title) && (
                              <a data-testid={`day-${day.day}-book-${bi}`} target="_blank" rel="noreferrer"
                                href={bookingUrl(b.place || b.title, data.inputs?.area || g.region || data.inputs?.location)}
                                className="inline-flex items-center gap-1 text-xs text-terracotta hover:underline mt-2">
                                <ExternalLink className="w-3 h-3" /> Find &amp; book
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {day.tips?.length > 0 && (
                  <div className="rounded-2xl bg-sage p-3">
                    <p className="text-[11px] uppercase tracking-[0.15em] text-stone mb-1.5">When a craving hits</p>
                    <ul className="space-y-1">
                      {day.tips.map((tip, ti) => (
                        <li key={ti} className="text-sm text-olive/80 flex gap-2"><span className="text-gold">•</span>{tip}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Scripture + encouragement */}
      {(s.scripture || s.encouragement) && (
        <div className="rounded-3xl bg-brand-navy-light text-brand-ivory border border-brand-gold/20 p-8 shadow-soft relative overflow-hidden" data-testid="strategy-encouragement">
          <Quote className="absolute -top-2 -left-1 w-20 h-20 text-brand-gold/10" />
          {s.scripture && (
            <>
              <p className="font-scripture italic text-scripture-lg leading-relaxed">“{s.scripture.text}”</p>
              <p className="text-sm text-brand-gold mt-3 mb-5">{s.scripture.ref}</p>
            </>
          )}
          {s.encouragement && <p className="text-brand-ivory-muted leading-relaxed max-w-2xl">{s.encouragement}</p>}
        </div>
      )}
    </div>
  );
}
